import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Form, FormSelect, FormGroup, FormControl, Placeholder, Spinner } from "react-bootstrap";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import * as Sentry from "@sentry/react";

//services
import { validate } from "../services/helpers";

import AlertDialog from "./AlertDialog";
import JobSelectorDropdown from "./JobSelectorDropdown";
import StatusBadge from "./StatusBadge";
import useUserAccount from "../hooks/useUserAccount";
import useMessages from "../hooks/useMessages";
import useFormatter from "../hooks/useFormatter";
import useJobs from "../hooks/useJobs";
import MessageFormField from "./MessageFormField";

// strings for translations
const messages = defineMessages({
    back: {
        id: "Palaa takaisin",
        defaultMessage: "Palaa takaisin"
    },
    title: {
        id: "Aloita uusi viestiketju",
        defaultMessage: "Aloita uusi viestiketju"
    },
    chooseTopicLabel: {
        id: "Valitse, mitä asiasi koskee.",
        defaultMessage: "Valitse, mitä asiasi koskee."
    },
    chooseTopic: {
        id: "Valitse aihe",
        defaultMessage: "Valitse aihe"
    },
    send: {
        id: "Lähetä",
        defaultMessage: "Lähetä"
    },
    authError: {
        id: 'Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.',
        defaultMessage: 'Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.'
    },
    notFound: {
        id: "Yhteydenottolomakkeita ei ole saatavilla juuri nyt. Yritä myöhemmin uudelleen.",
        defaultMessage: "Yhteydenottolomakkeita ei ole saatavilla juuri nyt. Yritä myöhemmin uudelleen."
    },
    errorOccurredTryAgain: {
        id: "Tapahtui virhe. Ole hyvä ja yritä uudelleen.",
        defaultMessage: "Tapahtui virhe. Ole hyvä ja yritä uudelleen."
    },
    emailOrSmsRequired: {
        id: "Sinun tulee ilmoittaa joko sähköpostiosoite tai puhelinnumero.",
        defaultMessage: "Sinun tulee ilmoittaa joko sähköpostiosoite tai puhelinnumero."
    },
    modalCLoseHeader: {
        id: "Poistu tallentamatta",
        defaultMessage: "Poistu tallentamatta"
    },
    modalCloseConfirmation: {
        id: "Viestissäsi on tallentamattomia muutoksia. Oletko varma, että haluat sulkea viestin ja hylätä luonnoksen?",
        defaultMessage: "Viestissäsi on tallentamattomia muutoksia. Oletko varma, että haluat sulkea viestin ja hylätä luonnoksen?"
    },
    modalCloseClose: {
        id: "Sulje ja hylkää luonnos",
        defaultMessage: "Sulje ja hylkää luonnos"
    },
    modalCloseKeepEdit: {
        id: "Jatka kirjoittamista",
        defaultMessage: "Jatka kirjoittamista"
    },
    modalClose: {
        id: "Sulje",
        defaultMessage: "Sulje"
    }
});

export { messages };

const skeletonCount = 5;

const MessageCenterModal = (props) => {
    const lng = props.intl.formatMessage;
    const [forms, setForms] = useState([]);
    const [alert, setAlert] = useState(null);
    const [formFields, setFormFields] = useState([]);
    const [inputValues, setInputValues] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [loadingFormContent, setLoadingFormContent] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [attachmentsEnabled, setAttachmentsEnabled] = useState(false);
    const [userNameFromUserData, setUserNameFromUserData] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isJobPreselected = !!props.job;
    const [selectedJob, setSelectedJob] = useState(isJobPreselected ? props.job : null);
    const jobless = !selectedJob;
    const { userIsPerson, userName, userCompanyName } = useUserAccount();
    const { fetchForms, fetchFormFields, createTicket } = useMessages();
    const { formatCurrency } = useFormatter();
    const { getJobCurrency } = useJobs();
    const backendRequirements = ['request_type_id', 'summary', 'description', 'notification_email', 'notification_sms', 'job_id', 'dropdown_value']; // backend requires these keys to work

    const headingRef = useRef(null);

    useEffect(() => {
        // get available message center forms, jobless forms differ from forms tied to a job
        setFormFields([]);
        setAttachmentsEnabled(false);
        void getForms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobless]);

    useEffect(() => {
        if (confirmClose && headingRef.current) {
            headingRef.current.focus();
        }
    }, [confirmClose]);

    /**
     * Get available message center form templates
     */
    const getForms = async () => {
        // clear any error messages
        setAlert(null);
        try {
            const formOptions = await fetchForms(jobless);
            setForms(formOptions)
        } catch (error) {
            if (error.response?.status === 404) {
                setAlert({
                    message: lng(messages.notFound),
                    type: 'warning'
                });
            } else {
                setAlert({
                    message: lng(messages.errorOccurredTryAgain),
                    type: 'danger'
                });
            }
        }
    }

    /**
     * Get contents/fields of a single form template.
     */
    const getFormContents = async (formId) => {
        // clear any error messages
        setAlert(null);

        if (!formId || formId === '') {
            setFormFields([]);
            setAttachmentsEnabled(false);
            return;
        }
        // add job (if one is selected) and form id for later use
        setInputValues(jobless ? { 'request_type_id': formId } : { 'job_id': selectedJob.jobid, 'request_type_id': formId });
        // show skeleton placeholders while getting content
        setLoadingFormContent(true);

        try {
            const fields = await fetchFormFields(formId);
            setFormFields(modifyResponseData(fields));

            // toggle skeletons off
            setLoadingFormContent(false);

            // Find the form object that matches the given formId
            const form = forms.find(form => form.form_id === formId);

            // Extract helptext from the found form
            const formDetails = form ? form.help_text : '';

            if (formDetails !== '') {
                setAlert({
                    message: formDetails,
                    type: 'primary'
                });
            }
        } catch (error) {
            // toggle skeletons off
            setLoadingFormContent(false);

            if (error?.response?.status === 404) {
                // forms not found
                setAlert({
                    message: lng(messages.notFound),
                    type: 'warning'
                });
            } else {
                setAlert({
                    message: lng(messages.errorOccurredTryAgain),
                    type: 'danger'
                });
            }
        }
    }

    /**
     * Check if user name is available for person
     * @returns {boolean}
     */
    function canGetUserName() {
        const name = userName();
        return (typeof name !== 'undefined' && name !== '');
    }

    /**
     * Check for modification needs in data, e.g. change customfields field_id's to meet backend requirements.
     *
     * @param {*} data
     */
    function modifyResponseData(data) {
        let modifiedData = data;

        // loop data to check if any changes to fields should be done
        modifiedData.forEach((item, index, object) => {
            if (item.field_id === 'attachment') {
                // Remove attachment field and enable it as part of the description component
                object.splice(index, 1);
                setAttachmentsEnabled(true);
            } else if (item.field_id === 'end_customer_name' && canGetUserName()) {
                // Remove Name field for person, if name is available
                object.splice(index, 1);
                setUserNameFromUserData(true);
            }
        });

        return modifiedData;
    }


    /**
     * Handle changes in input and remove errors.
     *
     * @param {*} e
     */
    function handleInputs(e) {
        // remove validation errors
        if (e.target.checkValidity()) {
            e.target.classList.remove("is-invalid");
        }
        setDirty(true);
        setInputValues({
            ...inputValues,
            [e.target.id]: e.target.value
        });
    }

    /**
     * Check whether the form has unsaved changes before closing it.
     *
     * Display a confirmation, if there are unsaved changes (message will be discarded).
     * Close the form without additional confirmations, if nothing has been entered.
     */
    function handleHide() {
        dirty ? setConfirmClose(true) : resetValuesAndClose();
    }

    /**
     * Map data for submit purposes.
     * 
     * If the form contains fields that do not meet the backend requirements, 
     * add the content of those fields to 'description'.
     * 
     * If form has a input with options, add that option value to dropdown_value,
     * otherwise it must be kept null.
     * 
     *  
     * @param {*} data 
     */
    function mapDataForSubmit(data) {
        let requestData = {
            request_type_id: data.request_type_id,
            summary: data.summary,
            description: data.description,
            notification_email: data.notification_email,
            notification_sms: data.notification_sms,
            job_id: data.job_id,
            dropdown_value: data.dropdown_value,
            custom_fields: {}
        };

        // go through each field
        for (const [key, value] of Object.entries(data)) {

            if (!backendRequirements.includes(key)) {

                // if there are fields that don't meet the backend requirement, add them to custom fields
                if (key !== 'object_ids') {
                    requestData.custom_fields[key] = value;
                } else {
                    const label = document.querySelector("label[for=" + key + "]").innerText ?? key;
                    // if there are fields that don't meet the backend requirement, add user input to description 
                    requestData.description += " \n" + label + ": " + value;
                }
            }
        }

        let objectIds = [];

        // Add object_ids for attachments
        for (const attachment of attachments) {
            if (typeof attachment.objectid !== 'undefined' && attachment.objectid !== '') {
                objectIds.push(attachment.objectid);
            }
        }
        requestData.object_ids = objectIds;

        // Add user name to custom fields from user data, if needed
        if (userNameFromUserData) {
            if (userIsPerson()) {
                // B2C e.g. John Doe
                requestData.custom_fields['end_customer_name'] = userName();
            } else {
                // B2B e.g. Company Inc. (John Doe)
                requestData.custom_fields['end_customer_name'] = userCompanyName() + '(' + userName() + ')';
            }
        }

        // return request data that is ready for submit
        return requestData;
    }

    /**
     * Create the ticket and, on success, reset/close the modal and notify the caller.
     * On failure, show an error alert without closing the modal.
     *
     * @param {*} data
     */
    async function submitTicket(data) {
        setSubmitting(true);
        try {
            await createTicket(data);
            resetValuesAndClose();
            props.onSuccess?.();
        } catch (error) {
            Sentry.captureException(error);
            setAlert({
                message: lng(messages.errorOccurredTryAgain),
                type: 'danger'
            });
        } finally {
            setSubmitting(false);
        }
    }

    /**
     * Handle submitting the form. Check that form passes validation
     * and then prepare the data for submit.
     *
     * @param {*} e
     */
    function handleSubmit(e) {
        e.preventDefault();
        setAlert(null);

        // Custom validation for 'tel' and 'email' fields
        let isValid = validate(e.currentTarget);

        let isTelOrEmailFilled = false;

        formFields.forEach((i) => {
            let type = i.type;

            if (i.field_id.toLowerCase() === 'notification_sms') {
                type = 'tel';
            } else if (i.field_id.toLowerCase() === 'notification_email') {
                type = 'email';
            }

            if (type === 'tel' || type === 'email') {
                if (inputValues[i.field_id]) {
                    isTelOrEmailFilled = true;
                }
            }

            if (i.field_id === 'dropdown_value') {
                if (inputValues[i.field_id]) {
                    document.getElementById("dropdown_value")?.classList.remove("is-invalid");
                } else {
                    document.getElementById("dropdown_value")?.classList.add("is-invalid");
                }
            }
        });

        if (!isTelOrEmailFilled) {
            isValid = false;
            setAlert({
                message: lng(messages.emailOrSmsRequired),
                type: 'danger'
            });

            document.getElementById("notification_email").classList.add("is-invalid");
            document.getElementById("notification_sms").classList.add("is-invalid");
        }

        if (isValid) {
            // prepare data for submit
            const data = mapDataForSubmit(inputValues);
            void submitTicket(data);
        }
    }

    /**
     * Reset form values and close the modal
     */
    function resetValuesAndClose() {
        // clear alerts, dropdown and input values
        setAlert(null);
        setFormFields([]);
        setInputValues({});
        setAttachments([]);
        setAttachmentsEnabled(false);
        setDirty(false);

        // ensure close confirmation if hidden and close the modal
        setConfirmClose(false);
        props.onClose();
    }

    const confirmCloseElement = (
        <div>
            <h3 tabIndex="-1" ref={headingRef}>{lng(messages.modalCLoseHeader)}</h3>
            <div className="mt-4">
                <h5>{lng(messages.modalCloseConfirmation)}</h5>
            </div>
            <div className="d-grid d-sm-block mt-3">
                <Button
                    id="close-confirmation-close"
                    onClick={resetValuesAndClose}
                    aria-label={lng(messages.modalCloseClose)}
                >
                    {lng(messages.modalCloseClose)}
                </Button>
                <Button
                    id="close-confirmation-cancel"
                    onClick={() => setConfirmClose(false)}
                    variant="tertiary"
                    aria-label={lng(messages.modalCloseKeepEdit)}
                >
                    {lng(messages.modalCloseKeepEdit)}
                </Button>
            </div>
        </div>
    )

    const modalHeaderElement = (
        <Modal.Header closeButton closeLabel={lng(messages.modalClose)}>
            <Button
                className="modal-back"
                onClick={handleHide}
                aria-label={lng(messages.back)}
            >
                <FontAwesomeIcon icon={far.faArrowLeft} />
                {lng(messages.back)}
            </Button>
            <Modal.Title as="h4" id="modal-title-message-center-forms">
                {lng(messages.title)}
            </Modal.Title>
        </Modal.Header>
    )

    const formTopicSelectorElement = (
        <FormGroup className="form-floating">
            <FormSelect id="message-center-form-option" className="form-control mb-3" onChange={(e) => getFormContents(e.target.value)} placeholder={lng(messages.chooseTopicLabel)}  >
                <option id={lng(messages.chooseTopic)} key={lng(messages.chooseTopic)} value="" >{lng(messages.chooseTopic)}</option>
                {forms.map(option => (
                    <option id={option.form_id} key={option.form_id} value={option.form_id}>{option.form_name}</option>
                ))}
            </FormSelect>
            <Form.Label htmlFor='message-center-form-option'>{lng(messages.chooseTopicLabel)}</Form.Label>
            <Form.Control.Feedback type="invalid" />
        </FormGroup>
    )

    const skeletonElement = (
        <div>
            {new Array(skeletonCount).fill(null).map((value, index) => (
                <Placeholder as={FormGroup} animation="glow" className="mb-3" style={{ opacity: 1 / (index + 1) }} key={"skeleton-input-" + index}>
                    <Placeholder as={FormControl} ></Placeholder>
                </Placeholder>
            ))}
        </div>
    )

    return (
        <Modal id="message-center-modal" show={props.show} dialogClassName="message-center-form" onHide={handleHide} aria-labelledby="modal-title-message-center-forms" size={'lg'} backdrop="static">
            <div className="form-modal-parent">
                <div className={confirmClose ? "offcanvas-modal offcanvas offcanvas-start show " : ""}>
                    {confirmClose && confirmCloseElement}
                </div>
                {modalHeaderElement}
                <Modal.Body>
                    {selectedJob && (
                        <div className="form-modal-sub-title d-flex gap-2 flex-wrap mb-3">
                            <div><strong>{selectedJob.payee?.name}</strong></div>
                            {selectedJob.openamount > 0 && (
                                <div>{formatCurrency(selectedJob.openamount, getJobCurrency(selectedJob))}</div>
                            )}
                            <StatusBadge statusCode={selectedJob.job_main_statuscode} dueDate={selectedJob.paydate} />
                        </div>
                    )}
                    {alert && (
                        <AlertDialog
                            message={alert.message}
                            type={alert.type}
                        />
                    )}
                    <Form id="message-center-modal-form" onSubmit={handleSubmit} noValidate>
                        <JobSelectorDropdown
                            job={props.job}
                            value={selectedJob}
                            onChange={setSelectedJob}
                        />
                        { forms.length > 0 && formTopicSelectorElement }
                        { loadingFormContent && skeletonElement }
                        { !loadingFormContent && formFields.map(field => (
                            <MessageFormField
                                key={field.field_id}
                                field={field}
                                handleInputs={handleInputs}
                                inputValues={inputValues}
                                attachmentsEnabled={attachmentsEnabled}
                                handleAttachments={setAttachments}
                            />
                        ))}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        id="message-center-modal-form-send"
                        form="message-center-modal-form"
                        variant="primary"
                        type="submit"
                        disabled={formFields.length <= 0 || submitting}
                        aria-label={lng(messages.send)}
                    >
                        {submitting && (<Spinner animation="border" size="sm" />)} {lng(messages.send)}
                    </Button>
                </Modal.Footer>
            </div>
        </Modal>
    )
};

MessageCenterModal.propTypes = {
    'intl': PropTypes.object.isRequired,
    'show': PropTypes.bool,
    'onClose': PropTypes.func.isRequired,
    'onSuccess': PropTypes.func,
    'job': PropTypes.object
};

export default injectIntl(MessageCenterModal);
